import { useState, useEffect } from 'react';
import { X } from '@phosphor-icons/react';
import BottomSheet from '../shared/BottomSheet';

// Single-restaurant: los filtros actúan sobre los PRODUCTOS del menú, no sobre
// negocios. Por eso no existen "tiempo de envío" ni "compra mínima" (con un solo
// restaurante siempre darían el mismo resultado).
const DEFAULT_FILTERS = {
  sortBy: 'relevance', // relevance | priceAsc | priceDesc
  onlyBestseller: false,
  onlyDiscount: false,
  // null = sin filtro. Con valor, deja los platillos cuyo precio sea <= valor.
  maxPrice: null,
};

// Opciones de rango. Compartidas con FilterPills y RangeFilterSheet.
const MAX_PRICE_OPTIONS = [
  { value: 69, label: '$69 o menos' },
  { value: 100, label: '$100 o menos' },
  { value: 150, label: '$150 o menos' },
];

const CATEGORIES = [
  { key: 'sort',     label: 'Ordenar' },
  { key: 'maxPrice', label: 'Precio' },
  { key: 'bestseller', label: 'Más vendidos' },
  { key: 'discount', label: 'Descuento' },
];

/**
 * Modal de filtros estilo bottom-sheet con layout de 2 columnas:
 *   - Izquierda: categorías (Ordenar, Precio, Más vendidos, Descuento)
 *   - Derecha: opciones de la categoría seleccionada (radio buttons)
 *   - Footer: "Limpiar filtros" + "Aplicar"
 *
 * El draft state se mantiene local — solo al "Aplicar" se notifica al parent.
 * Usa el BottomSheet shared (body scroll lock + animación + backdrop).
 *
 * Props:
 *   isOpen, onClose
 *   filters: estado actual (para inicializar draft)
 *   onApply: (newFilters) => void
 *   topOffset: pixels desde el top del viewport donde empieza el sheet
 *              (para no tapar las stickys del fondo)
 */
export default function FiltersModal({ isOpen, onClose, filters, onApply, topOffset = 0 }) {
  const [draft, setDraft] = useState(filters || DEFAULT_FILTERS);
  const [activeCategory, setActiveCategory] = useState('sort');

  // Cada vez que se abre, sincroniza draft con los filtros aplicados.
  useEffect(() => {
    if (isOpen) {
      setDraft(filters || DEFAULT_FILTERS);
      setActiveCategory('sort');
    }
  }, [isOpen, filters]);

  const handleApply = () => {
    onApply(draft);
    onClose();
  };

  const handleClear = () => setDraft(DEFAULT_FILTERS);

  // maxHeight calculado para que el sheet termine arriba en topOffset.
  const maxHeight = `calc(100dvh - ${topOffset}px)`;

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} zIndex={60} maxHeight={maxHeight}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100">
        <h2 className="text-xl font-bold font-display text-gray-900">Filtros</h2>
        <button
          type="button"
          onClick={onClose}
          className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center active:scale-95 transition-transform"
          aria-label="Cerrar"
        >
          <X size={18} weight="bold" className="text-gray-700" />
        </button>
      </div>

      {/* Body: 2 columnas */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar de categorías */}
        <div className="w-2/5 border-r border-gray-100 overflow-y-auto">
          {CATEGORIES.map((cat) => {
            const isActive = activeCategory === cat.key;
            return (
              <button
                key={cat.key}
                type="button"
                onClick={() => setActiveCategory(cat.key)}
                className={`w-full text-left px-5 py-4 text-sm font-semibold relative ${
                  isActive ? 'text-cta-bg bg-gray-50' : 'text-gray-700'
                }`}
              >
                {isActive && (
                  <span className="absolute left-0 top-0 bottom-0 w-1 bg-cta-bg" />
                )}
                {cat.label}
              </button>
            );
          })}
        </div>

        {/* Opciones de la categoría activa */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {activeCategory === 'sort' && (
            <CategoryOptions
              label="ORDENAR POR"
              options={[
                { value: 'relevance', label: 'Relevancia (Default)' },
                { value: 'priceAsc',  label: 'Precio: menor a mayor' },
                { value: 'priceDesc', label: 'Precio: mayor a menor' },
              ]}
              selected={draft.sortBy}
              onChange={(v) => setDraft((d) => ({ ...d, sortBy: v }))}
            />
          )}
          {activeCategory === 'maxPrice' && (
            <CategoryOptions
              label="PRECIO DEL PLATILLO"
              options={[
                { value: null, label: 'Cualquiera' },
                ...MAX_PRICE_OPTIONS,
              ]}
              selected={draft.maxPrice}
              onChange={(v) => setDraft((d) => ({ ...d, maxPrice: v }))}
            />
          )}
          {activeCategory === 'bestseller' && (
            <CategoryOptions
              label="MÁS VENDIDOS"
              options={[
                { value: false, label: 'Todos los platillos' },
                { value: true,  label: 'Solo los más vendidos' },
              ]}
              selected={draft.onlyBestseller}
              onChange={(v) => setDraft((d) => ({ ...d, onlyBestseller: v }))}
            />
          )}
          {activeCategory === 'discount' && (
            <CategoryOptions
              label="DESCUENTO"
              options={[
                { value: false, label: 'Todos los platillos' },
                { value: true,  label: 'Solo con descuento' },
              ]}
              selected={draft.onlyDiscount}
              onChange={(v) => setDraft((d) => ({ ...d, onlyDiscount: v }))}
            />
          )}
        </div>
      </div>

      {/* Footer */}
      <div
        className="flex gap-3 px-5 pt-3 border-t border-gray-100"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}
      >
        <button
          type="button"
          onClick={handleClear}
          className="flex-1 py-3 text-sm font-bold text-gray-700 active:scale-[0.98] transition-transform"
        >
          Limpiar filtros
        </button>
        <button
          type="button"
          onClick={handleApply}
          className="flex-1 bg-cta-bg hover:bg-cta-hover text-white rounded-full py-3 text-sm font-bold active:scale-[0.98] transition-transform"
        >
          Aplicar
        </button>
      </div>
    </BottomSheet>
  );
}

function CategoryOptions({ label, options, selected, onChange }) {
  return (
    <div>
      <p className="text-[11px] font-bold tracking-wider text-gray-500 mb-3">{label}</p>
      <div className="flex flex-col gap-3">
        {options.map((opt) => {
          const isSelected = selected === opt.value;
          return (
            <button
              key={String(opt.value)}
              type="button"
              onClick={() => onChange(opt.value)}
              className="flex items-center gap-3 py-1 text-left active:scale-[0.99] transition-transform"
            >
              <span
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  isSelected ? 'border-cta-bg' : 'border-gray-300'
                }`}
              >
                {isSelected && <span className="w-2.5 h-2.5 rounded-full bg-cta-bg" />}
              </span>
              <span className={`text-sm ${isSelected ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                {opt.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export { DEFAULT_FILTERS, MAX_PRICE_OPTIONS };
